import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Mail, ChevronLeft, ShieldCheck, Lock, Users, User, RefreshCw, LogOut, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../../utils/api';
import { Modal, Button, Field, Input, Select, ConfirmModal, errMsg } from '../SupportCoordination/kit';
import MailEditor from './MailEditor';
import { fullTime } from './mailUi';

export const VISIBILITY = [
  { value: 'owner', label: 'Only me', hint: 'No one else in the company sees this mail.', icon: Lock },
  { value: 'admins', label: 'Me and admins', hint: 'Admins can read it too (the usual choice for a work mailbox).', icon: ShieldCheck },
  { value: 'team', label: 'Everyone in the office', hint: 'A shared mailbox, such as info@ or intake@.', icon: Users },
];
const COLORS = ['#0f6cbd', '#8764b8', '#038387', '#ca5010', '#c239b3', '#498205', '#986f0b', '#d13438'];

// The two big sign-in buttons draw the providers' own marks (simple shapes, no logos fetched).
function MicrosoftMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden><rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" /><rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" /></svg>
  );
}
function GoogleMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.2-.1-2.3-.4-3.5z" />
    </svg>
  );
}

export async function startOAuth(provider, visibility) {
  const r = await api.post(`/mail/accounts/oauth/${provider}/start`, { visibility });
  window.location.assign(r.data.url);
}

function VisibilityPicker({ value, onChange }) {
  return (
    <div role="radiogroup" aria-label="Who can see this mailbox" className="grid gap-2 sm:grid-cols-3">
      {VISIBILITY.map((v) => (
        <button key={v.value} type="button" role="radio" aria-checked={value === v.value} onClick={() => onChange(v.value)}
          className={clsx('text-left rounded-xl px-3 py-2.5 ring-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0f6cbd]',
            value === v.value ? 'ring-[#0f6cbd] bg-sky-50/70' : 'ring-slate-200 hover:bg-slate-50')}>
          <span className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-900"><v.icon size={14} className={value === v.value ? 'text-[#0f6cbd]' : 'text-slate-500'} aria-hidden />{v.label}</span>
          <span className="block text-[11.5px] leading-snug text-slate-500 mt-0.5">{v.hint}</span>
        </button>
      ))}
    </div>
  );
}

export function AddAccountDialog({ open, onClose, status, onAdded }) {
  const [step, setStep] = useState('choose');
  const [visibility, setVisibility] = useState('admins');
  const [busy, setBusy] = useState(null);
  const [f, setF] = useState({ email: '', password: '', display_name: '', imap_host: '', imap_port: '993', smtp_host: '', smtp_port: '465', user: '' });
  const [advanced, setAdvanced] = useState(false);
  useEffect(() => { if (open) { setStep('choose'); setBusy(null); setAdvanced(false); setF((x) => ({ ...x, password: '' })); } }, [open]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const domain = (f.email.split('@')[1] || '').toLowerCase();
  // The mail server, found from the address alone: { state: idle|looking|found|microsoft|none, ... }.
  const [found, setFound] = useState({ state: 'idle' });
  useEffect(() => {
    const email = f.email.trim();
    if (step !== 'imap' || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { setFound({ state: 'idle' }); return undefined; }
    let alive = true;
    setFound({ state: 'looking' });
    const t = setTimeout(() => {
      api.get('/mail/discover', { params: { email } }).then((r) => {
        if (!alive) return;
        const d = r.data || {};
        if (d.microsoft) setFound({ state: 'microsoft' });
        else if (d.found) {
          setFound({ state: 'found', ...d });
          setF((x) => ({ ...x, imap_host: d.imap.host, imap_port: String(d.imap.port), smtp_host: d.smtp.host, smtp_port: String(d.smtp.port) }));
        } else { setFound({ state: 'none' }); setAdvanced(true); }
      }).catch(() => { if (alive) { setFound({ state: 'none' }); setAdvanced(true); } });
    }, 500);
    return () => { alive = false; clearTimeout(t); };
  }, [f.email, step]);

  const signIn = async (provider) => {
    setBusy(provider);
    try { await startOAuth(provider, visibility); } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };
  const connect = async () => {
    if (!f.email.trim() || !f.password) { toast.error('Enter the email address and its password.'); return; }
    if (found.state === 'microsoft') { toast.error('This is a Microsoft 365 mailbox. Use Sign in with Microsoft.'); return; }
    setBusy('imap');
    try {
      const body = { email: f.email.trim(), password: f.password, display_name: f.display_name.trim() || undefined, visibility };
      if (advanced) {
        body.imap = { host: f.imap_host.trim(), port: Number(f.imap_port), secure: Number(f.imap_port) === 993, user: f.user.trim() || undefined };
        body.smtp = { host: f.smtp_host.trim(), port: Number(f.smtp_port), secure: Number(f.smtp_port) === 465, user: f.user.trim() || undefined };
      }
      const r = await api.post('/mail/accounts/imap', body);
      toast.success(`${f.email.trim()} is connected`);
      onAdded && onAdded(r.data.id);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const msReady = !!(status && status.microsoft);
  const gReady = !!(status && status.google);
  return (
    <Modal open={open} onClose={onClose} width="lg"
      title={step === 'choose' ? 'Add a mailbox' : 'Connect another email'}
      sub={step === 'choose' ? 'Your mail stays in your mailbox. Rostery shows it here and sends from it.' : 'Most providers need an app password rather than your normal password.'}
      footer={step === 'imap' && (
        <>
          <Button variant="secondary" onClick={() => setStep('choose')} icon={ChevronLeft}>Back</Button>
          <Button onClick={connect} loading={busy === 'imap'} disabled={found.state === 'microsoft'}>Connect</Button>
        </>
      )}>
      {step === 'choose' ? (
        <div className="space-y-5 pt-1">
          <div className="space-y-2.5">
            <ProviderButton mark={<MicrosoftMark />} title="Sign in with Microsoft" sub="Outlook, Microsoft 365, Hotmail and Live" ready={msReady} loading={busy === 'microsoft'} onClick={() => signIn('microsoft')}
              notReady="An admin first adds the Microsoft app details in Plan Management › Settings › Email & Mailbox." />
            <ProviderButton mark={<GoogleMark />} title="Sign in with Google" sub="Gmail and Google Workspace" ready={gReady} loading={busy === 'google'} onClick={() => signIn('google')}
              notReady="Google sign-in is not switched on yet. You can still add Gmail below with an app password." />
            <ProviderButton mark={<Mail size={20} className="text-slate-600" aria-hidden />} title="Other email" sub="Gmail with an app password, Yahoo, iCloud, Zoho, Telstra, your own domain (IMAP)" ready onClick={() => setStep('imap')} />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-slate-800 mb-2">Who can see this mailbox in Rostery</p>
            <VisibilityPicker value={visibility} onChange={setVisibility} />
          </div>
        </div>
      ) : (
        <form className="space-y-3.5" onSubmit={(e) => { e.preventDefault(); connect(); }}>
          <Field label="Email address" required><Input type="email" autoComplete="off" value={f.email} onChange={set('email')} placeholder="you@yourcompany.com.au" autoFocus /></Field>
          {found.state === 'looking' && <p className="flex items-center gap-2 text-[12.5px] text-slate-600"><RefreshCw size={13} className="animate-spin" aria-hidden />Finding the mail server for {domain}…</p>}
          {found.state === 'found' && (
            <div className="flex gap-2 text-[12.5px] text-emerald-900 bg-emerald-50 ring-1 ring-emerald-200/70 rounded-lg px-3 py-2">
              <CheckCircle2 size={15} className="mt-0.5 flex-shrink-0 text-emerald-600" aria-hidden />
              <div>
                Found the mail server for {domain}: <b>{found.imap.host}</b>. Just enter the password.
                {found.note && <span className="block text-emerald-800/80 mt-0.5">{found.note}</span>}
                {/(^|\.)gmail\.com$/i.test(found.imap.host) && (
                  <span className="block mt-1.5 text-emerald-900">
                    Gmail does not accept your normal password here.{' '}
                    <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="font-semibold underline">Create a Google app password</a>
                    {' '}(2-Step Verification must be on), then paste the 16-letter code below.
                  </span>
                )}
              </div>
            </div>
          )}
          {found.state === 'microsoft' && (
            <div className="text-[12.5px] text-amber-900 bg-amber-50 ring-1 ring-amber-200/70 rounded-lg px-3 py-2.5">
              <p className="flex gap-2"><AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden />{domain} uses Microsoft 365. Microsoft only lets these mailboxes in by signing in, not with a password.</p>
              {status && status.microsoft && <div className="mt-2"><Button size="sm" onClick={() => signIn('microsoft')} loading={busy === 'microsoft'}>Sign in with Microsoft</Button></div>}
            </div>
          )}
          {found.state === 'none' && <p className="text-[12.5px] text-slate-600 bg-slate-50 rounded-lg px-3 py-2">Rostery could not find the mail server for {domain} by itself. Enter the server names below (your email or website host lists them).</p>}
          <Field label="Password or app password" required hint="Rostery keeps it encrypted and uses it only to read and send this mail.">
            <Input type="password" autoComplete="new-password" value={f.password} onChange={set('password')} />
          </Field>
          <Field label="Your name, as people see it" hint="Optional"><Input value={f.display_name} onChange={set('display_name')} placeholder="Jane Citizen" /></Field>
          <div>
            <p className="text-[13px] font-semibold text-slate-800 mb-2">Who can see this mailbox</p>
            <VisibilityPicker value={visibility} onChange={setVisibility} />
          </div>
          {found.state === 'microsoft' ? null : !advanced ? (
            <button type="button" onClick={() => setAdvanced(true)} className="text-[12.5px] text-[#0f6cbd] hover:underline">Server settings</button>
          ) : (
            <fieldset className="rounded-xl ring-1 ring-slate-200 p-3.5 space-y-3">
              <legend className="px-1 text-[12.5px] font-semibold text-slate-700">Server settings (your email provider lists these)</legend>
              <div className="grid grid-cols-3 gap-2.5">
                <Field label="Incoming (IMAP) server" className="col-span-2"><Input value={f.imap_host} onChange={set('imap_host')} placeholder="imap.example.com" /></Field>
                <Field label="Port"><Select value={f.imap_port} onChange={(v) => setF((x) => ({ ...x, imap_port: v }))} options={[{ value: '993', label: '993 (SSL)' }, { value: '143', label: '143 (STARTTLS)' }]} /></Field>
                <Field label="Outgoing (SMTP) server" className="col-span-2"><Input value={f.smtp_host} onChange={set('smtp_host')} placeholder="smtp.example.com" /></Field>
                <Field label="Port"><Select value={f.smtp_port} onChange={(v) => setF((x) => ({ ...x, smtp_port: v }))} options={[{ value: '465', label: '465 (SSL)' }, { value: '587', label: '587 (STARTTLS)' }, { value: '2525', label: '2525' }, { value: '25', label: '25' }]} /></Field>
              </div>
              <Field label="Username" hint="Leave empty when it is the email address."><Input value={f.user} onChange={set('user')} autoComplete="off" /></Field>
            </fieldset>
          )}
          <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
        </form>
      )}
    </Modal>
  );
}

function ProviderButton({ mark, title, sub, ready, notReady, loading, onClick }) {
  return (
    <div>
      <button type="button" onClick={onClick} disabled={!ready || loading}
        className="w-full flex items-center gap-3.5 rounded-xl px-4 py-3 ring-1 ring-slate-200 bg-white text-left hover:bg-slate-50 hover:ring-slate-300 disabled:opacity-55 disabled:hover:bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0f6cbd]">
        <span className="w-10 h-10 rounded-lg bg-slate-50 ring-1 ring-slate-200/70 flex items-center justify-center flex-shrink-0">{loading ? <RefreshCw size={16} className="animate-spin text-slate-500" aria-hidden /> : mark}</span>
        <span className="min-w-0">
          <span className="block text-[14px] font-semibold text-slate-900">{title}</span>
          <span className="block text-[12.5px] text-slate-500">{sub}</span>
        </span>
      </button>
      {!ready && notReady && <p className="text-[12px] text-slate-500 mt-1.5 ml-1">{notReady}</p>}
    </div>
  );
}

export function AccountSettingsDialog({ account, me, onClose, onSaved, onRemoved }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (account) setF({ display_name: account.display_name || '', color: account.color || COLORS[0], visibility: account.visibility || 'admins', sync_days: String(account.sync_days || 30), signature_html: account.signature_html || '' });
  }, [account]);
  if (!account || !f) return null;
  const canEdit = !!(me && me.id === account.owner_user_id);
  const canRemove = !!(me && (['admin', 'super_admin'].includes(me.role) || me.id === account.owner_user_id));
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/mail/accounts/${account.id}`, { ...f, sync_days: Number(f.sync_days) });
      toast.success('Saved');
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const remove = async () => {
    await api.delete(`/mail/accounts/${account.id}`);
    toast.success(`Signed out of ${account.email}`);
    onRemoved && onRemoved();
    onClose();
  };
  const needsSignIn = account.status === 'reauth' && account.provider !== 'imap';
  return (
    <>
      <Modal open={!!account} onClose={onClose} width="lg" title={account.email}
        sub={`${account.provider === 'microsoft' ? 'Microsoft' : account.provider === 'google' ? 'Google' : 'IMAP'} mailbox${account.owner_name ? `, connected by ${account.owner_name}` : ''}${account.last_sync_at ? `. Last checked ${fullTime(account.last_sync_at)}` : ''}`}
        footer={(
          <>
            {canRemove && <Button variant="ghost" className="mr-auto text-rose-600" icon={LogOut} onClick={() => setConfirm(true)}>Sign out of this mailbox</Button>}
            <Button variant="secondary" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</Button>
            {canEdit && <Button onClick={save} loading={saving}>Save</Button>}
          </>
        )}>
        <div className="space-y-4">
          {(account.status === 'error' || needsSignIn) && (
            <div className="flex gap-2.5 rounded-xl bg-amber-50 ring-1 ring-amber-200/70 px-3.5 py-3 text-[13px] text-amber-900">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" aria-hidden />
              <div className="flex-1">
                {needsSignIn ? 'Rostery can no longer reach this mailbox. The sign-in has run out or was withdrawn.' : `The last check failed${account.last_error ? `: ${account.last_error}` : '.'}`}
                {needsSignIn && <div className="mt-2"><Button size="sm" onClick={() => startOAuth(account.provider, account.visibility).catch((e) => toast.error(errMsg(e)))}>Sign in again</Button></div>}
              </div>
            </div>
          )}
          {!canEdit && <p className="text-[13px] text-slate-600 bg-slate-50 rounded-lg px-3 py-2 flex items-center gap-2"><User size={14} aria-hidden />Only the person who connected this mailbox can change its settings.{canRemove ? ' As an admin you can sign it out of Rostery.' : ''}</p>}
          <fieldset disabled={!canEdit} className="space-y-4 disabled:opacity-70">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Your name, as people see it"><Input value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} /></Field>
              <Field label="Keep mail in Rostery for">
                <Select value={f.sync_days} onChange={(v) => setF({ ...f, sync_days: v })}
                  options={[7, 14, 30, 90, 180].map((d) => ({ value: String(d), label: `The last ${d} days` }))} />
              </Field>
            </div>
            <div>
              <p className="text-[13px] font-semibold text-slate-800 mb-2">Colour</p>
              <div className="flex gap-2" role="radiogroup" aria-label="Mailbox colour">
                {COLORS.map((c) => (
                  <button key={c} type="button" role="radio" aria-checked={f.color === c} aria-label={c} onClick={() => setF({ ...f, color: c })}
                    className={clsx('w-7 h-7 rounded-full ring-offset-2 transition', f.color === c ? 'ring-2 ring-slate-800' : 'hover:scale-110')} style={{ background: c }} />
                ))}
              </div>
            </div>
            <div>
              <p className="text-[13px] font-semibold text-slate-800 mb-2">Who can see this mailbox</p>
              <VisibilityPicker value={f.visibility} onChange={(v) => setF({ ...f, visibility: v })} />
            </div>
            <div>
              <p className="text-[13px] font-semibold text-slate-800 mb-1">Signature</p>
              <p className="text-[12px] text-slate-500 mb-2">Added to new mail, replies and forwards from this mailbox.</p>
              <div className="rounded-xl ring-1 ring-slate-200 overflow-hidden">
                <MailEditor value={f.signature_html} onChange={(v) => setF((x) => ({ ...x, signature_html: v }))} placeholder="Jane Citizen | Support Coordinator | 02 9000 0000" minHeight={110} />
              </div>
            </div>
          </fieldset>
        </div>
      </Modal>
      <ConfirmModal open={confirm} onClose={() => setConfirm(false)} danger title={`Sign out of ${account.email}?`} confirmLabel="Sign out" onConfirm={remove}>
        Rostery stops reading this mailbox, forgets its password or sign-in, and clears its copy of the mail. Nothing is deleted from the mailbox itself, and you can add it again any time.
      </ConfirmModal>
    </>
  );
}
